// The Moon's streamed data, on the same equirectangular tile grid as the
// Earth's imagery but with its own spans: a level-L tile covers 180 / 2^L
// degrees (L0 is 2 x 1 tiles, L7 is 256 x 128).
//
// Imagery: LROC WAC global mosaic (NASA/GSFC/Arizona State University, public
// domain), 303 px/deg, served by NASA Moon Trek as 256 px WMTS tiles. One
// 512 px project tile is the 2 x 2 Trek tiles one zoom deeper, so L7 is Trek's
// finest level (364 px/deg, about 83 m per pixel at the equator).
//
// Heights: LOLA (Lunar Orbiter Laser Altimeter) gridded elevation. L0-L5 are
// box-filtered from LDEM_64 (64 px/deg, PDS Geosciences Node); L6 and L7 are
// read from the USGS 118 m global mosaic (256 px/deg) with byte ranges, one
// uncompressed row strip at a time, and cleaned of its track stripes. A DEM tile is 257 x 257 posts with shared
// edges (post i of a tile at longitude -180 + (x + i/256) * span), heights in
// metres above the 1737.4 km reference sphere, packed into RGB by
// DEM_ENCODE so an 8-bit PNG carries them losslessly.
export const MOON_TILE_ROOT = '/textures/moon-tiles';

export const MOON_WAC_PATH = `${MOON_TILE_ROOT}/wac`;
// What the terrain streams: shading-free colour (the CGI Moon Kit map) with the
// WAC mosaic's detail at L5-L7 (scripts/planet-tile-cache.mjs). sRGB JPEG.
export const MOON_ALBEDO_PATH = `${MOON_TILE_ROOT}/albedo`;
export const MOON_ALBEDO_CACHE = 'moon-albedo-v2';
// Kit linear luminance L to normal albedo: 0.313 * L^0.628 (maria 0.07,
// highlands 0.16; fitted on Mare Tranquillitatis, Serenitatis, Imbrium and the
// Descartes, far-side and southern highlands).
export const KIT_ALBEDO = { a: 0.313, b: 0.628 };
export const MOON_WAC_CACHE = 'moon-wac-v1';
export const MOON_WAC_SOURCE = 'https://trek.nasa.gov/tiles/Moon/EQ/LRO_WAC_Mosaic_Global_303ppd_v02/1.0.0/default/default028mm';
export const MOON_WAC_MAX_LEVEL = 7;
// The streamed albedo goes to L9 (1456 px/deg, 21 m per pixel) with the
// Kaguya TC ortho mosaic's detail (scripts/planet-tile-cache.mjs).
export const MOON_ALBEDO_MAX_LEVEL = 9;
export const MOON_TC_SOURCE = 'https://trek.nasa.gov/tiles/Moon/EQ/Kaguya_TCortho_Mosaic_Global_4096ppd/1.0.0/default/default028mm';
export function moonTcSourceTiles(z, x, y) {
  const t = z + 1, out = [];
  for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) out.push(`${MOON_TC_SOURCE}/${t}/${2 * y + dy}/${2 * x + dx}.png`);
  return out;
}
// The Trek tiles making project tile (z, x, y), in [top-left, top-right,
// bottom-left, bottom-right] order.
export function moonWacSourceTiles(z, x, y) {
  const t = z + 1, out = [];
  for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) out.push(`${MOON_WAC_SOURCE}/${t}/${2 * y + dy}/${2 * x + dx}.jpg`);
  return out;
}

export const MOON_DEM_PATH = `${MOON_TILE_ROOT}/lola`;
export const MOON_DEM_CACHE = 'moon-lola-v1';
export const MOON_DEM_MAX_LEVEL = 7;
export const MOON_DEM_POSTS = 257;
export const MOON_RADIUS = 1737400;
// 0.05 m steps over -12 km .. +12 km (the Moon spans -9.1 .. +10.8 km).
export const DEM_ENCODE = { offset: 12000, scale: 20 };
export const LDEM_64_URL = 'https://pds-geosciences.wustl.edu/lro/lro-l-lola-3-rdr-v1/lrolol_1xxx/data/lola_gdr/cylindrical/img/ldem_64.img';
export const LDEM_118M_URL = 'https://asc-pds-services.s3.us-west-2.amazonaws.com/mosaic/Lunar_LRO_LOLA_Global_LDEM_118m_Mar2014.tif';
// Layout of the 118 m BigTIFF (read from its IFD): 92160 x 46080 int16 LE,
// one row per uncompressed strip, strips contiguous from ROW0, 0.5 m units,
// the grid's top-left corner at (-180, 90).
export const LDEM_118M = { width: 92160, height: 46080, ppd: 256, row0: 738233, scale: 0.5 };

export const moonSpan = (z) => 180 / 2 ** z;
export function validMoonTile(z, x, y, maxLevel) {
  return [z, x, y].every(Number.isInteger) && z >= 0 && z <= maxLevel && x >= 0 && x < 2 ** (z + 1) && y >= 0 && y < 2 ** z;
}

export function encodeHeight(h, out, i) {
  const v = Math.max(0, Math.min(16777215, Math.round((h + DEM_ENCODE.offset) * DEM_ENCODE.scale)));
  out[i] = v >> 16; out[i + 1] = (v >> 8) & 255; out[i + 2] = v & 255;
}
export function decodeHeight(r, g, b) {
  return (r * 65536 + g * 256 + b) / DEM_ENCODE.scale - DEM_ENCODE.offset;
}

// Heights (Float32Array, 257 x 257, row 0 north) of DEM tile (z, x, y) from
// the 118 m mosaic. readRows(row, col0, count) resolves an Int16Array of
// `count` samples starting at `col0` in raster row `row` (columns wrap).
//
// The 118 m grid is interpolated between LOLA's north-south ground tracks,
// which leaves stripes along the tracks and single-post speckle; under a low
// sun both read as ploughed furrows. Before sampling, a 3 x 3 median removes
// the speckle and a Gaussian smooths across the tracks (sigma 2 source
// pixels east-west, 1 north-south), which leaves real relief from about
// 350 m up. Then bilinear between the cleaned posts.
export async function demTileFrom118m(z, x, y, readRows, concurrency = 16) {
  const N = MOON_DEM_POSTS, span = moonSpan(z), S = LDEM_118M;
  const MX = 7, MY = 4;                         // source margins: median + Gaussian reach
  const out = new Float32Array(N * N);
  const cols = new Float64Array(N);
  let c0 = Infinity, c1 = -Infinity;
  for (let i = 0; i < N; i++) {
    const lon = -180 + (x + i / 256) * span;
    cols[i] = (lon + 180) * S.ppd - 0.5;
    c0 = Math.min(c0, Math.floor(cols[i])); c1 = Math.max(c1, Math.floor(cols[i]) + 1);
  }
  c0 -= MX; c1 += MX;
  const rowsFor = new Array(N);
  let r0 = Infinity, r1 = -Infinity;
  for (let j = 0; j < N; j++) {
    const lat = 90 - (y + j / 256) * span;
    const fr = Math.max(0, Math.min(S.height - 1.0001, (90 - lat) * S.ppd - 0.5));
    const r = Math.floor(fr);
    rowsFor[j] = [r, fr - r];
    r0 = Math.min(r0, r); r1 = Math.max(r1, r + 1);
  }
  r0 = Math.max(0, r0 - MY); r1 = Math.min(S.height - 1, r1 + MY);
  const W = c1 - c0 + 1, H = r1 - r0 + 1;
  const raw = new Float32Array(W * H);
  let k = 0;
  async function worker() {
    while (k < H) {
      const row = r0 + k++;
      const data = await readRows(row, c0, W);
      raw.set(data, (row - r0) * W);
    }
  }
  await Promise.all(Array.from({ length: concurrency }, worker));
  // 3 x 3 median.
  const med = new Float32Array(W * H), win = new Float32Array(9);
  for (let yy = 0; yy < H; yy++) for (let xx = 0; xx < W; xx++) {
    let n = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const sy = Math.max(0, Math.min(H - 1, yy + dy)), sx = Math.max(0, Math.min(W - 1, xx + dx));
      win[n++] = raw[sy * W + sx];
    }
    win.sort();
    med[yy * W + xx] = win[4];
  }
  // Separable Gaussian: sigma 2 across the tracks, 1 along them.
  const blur = (src, dst, horizontal, sigma) => {
    const rad = Math.ceil(sigma * 3), wts = [];
    let sum = 0;
    for (let t = -rad; t <= rad; t++) { const w = Math.exp(-(t * t) / (2 * sigma * sigma)); wts.push(w); sum += w; }
    for (let yy = 0; yy < H; yy++) for (let xx = 0; xx < W; xx++) {
      let v = 0;
      for (let t = -rad; t <= rad; t++) {
        const sx = horizontal ? Math.max(0, Math.min(W - 1, xx + t)) : xx;
        const sy = horizontal ? yy : Math.max(0, Math.min(H - 1, yy + t));
        v += src[sy * W + sx] * wts[t + rad];
      }
      dst[yy * W + xx] = v / sum;
    }
  };
  const tmp = new Float32Array(W * H), clean = new Float32Array(W * H);
  blur(med, tmp, true, 2);
  blur(tmp, clean, false, 1);
  for (let j = 0; j < N; j++) {
    const [r, fy] = rowsFor[j];
    const A = (r - r0) * W, B = (Math.min(S.height - 1, r + 1) - r0) * W;
    for (let i = 0; i < N; i++) {
      const fc = cols[i], c = Math.floor(fc), fx = fc - c;
      const a = c - c0, b = a + 1;
      const top = clean[A + a] * (1 - fx) + clean[A + b] * fx, bot = clean[B + a] * (1 - fx) + clean[B + b] * fx;
      out[j * N + i] = (top * (1 - fy) + bot * fy) * S.scale;
    }
  }
  return out;
}

// Heights of DEM tile (z, x, y) for z <= 5 from LDEM_64 (a Buffer or
// Uint8Array of the whole 23040 x 11520 int16 LE image, longitudes 0..360
// east), box-filtered over the post spacing so coarse levels do not alias.
export function demTileFromLdem64(z, x, y, bytes) {
  const W = 23040, H = 11520, PPD = 64, N = MOON_DEM_POSTS, span = moonSpan(z);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(N * N);
  const step = PPD * span / 256;              // source pixels between posts
  const half = Math.max(0, Math.floor(step / 2));
  for (let j = 0; j < N; j++) {
    const lat = 90 - (y + j / 256) * span;
    const fr = (90 - lat) * PPD - 0.5;
    for (let i = 0; i < N; i++) {
      const lon = -180 + (x + i / 256) * span;
      const fc = (((lon % 360) + 360) % 360) * PPD - 0.5;
      let sum = 0, n = 0;
      if (half === 0) {
        const r = Math.floor(fr), c = Math.floor(fc), fy = fr - r, fx = fc - c;
        const px = (rr, cc) => view.getInt16((Math.max(0, Math.min(H - 1, rr)) * W + (((cc % W) + W) % W)) * 2, true);
        const top = px(r, c) * (1 - fx) + px(r, c + 1) * fx, bot = px(r + 1, c) * (1 - fx) + px(r + 1, c + 1) * fx;
        out[j * N + i] = (top * (1 - fy) + bot * fy) * 0.5;
        continue;
      }
      const rc = Math.round(fr), cc = Math.round(fc);
      for (let dr = -half; dr <= half; dr++) {
        const rr = Math.max(0, Math.min(H - 1, rc + dr));
        for (let dc = -half; dc <= half; dc++) {
          sum += view.getInt16((rr * W + ((((cc + dc) % W) + W) % W)) * 2, true);
          n++;
        }
      }
      out[j * N + i] = sum / n * 0.5;
    }
  }
  return out;
}
